import { useState, useEffect } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Table, { Column } from '../components/Table';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import LookupSelect, { LookupOption } from '../components/LookupSelect';

interface Ticket {
  id: string;
  ticket_number: string;
  employee_name: string;
  subject: string;
  status: string;
  created_at: string;
}

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase())) : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

export default function FeedbackTickets() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<any>(null);
  const [replies, setReplies] = useState<any[]>([]);
  const [replyText, setReplyText] = useState('');
  const [replyStatus, setReplyStatus] = useState('Provide more data');
  const [managerId, setManagerId] = useState('');
  const [assignEmployeeIds, setAssignEmployeeIds] = useState('');
  const [showAssign, setShowAssign] = useState(false);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await api.get('/hr/feedback');
      setTickets(res.data.feedbacks || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const openDetail = async (ticket: Ticket) => {
    const res = await api.get(`/hr/feedback/${ticket.id}`);
    setDetail(res.data.ticket);
    setReplies(res.data.replies || []);
  };

  const sendReply = async () => {
    if (!detail) return;
    await api.put(`/hr/feedback/${detail.id}/reply`, { replyText, status: replyStatus });
    setReplyText('');
    await openDetail(detail);
    fetchTickets();
  };

  const saveAssignment = async () => {
    const employeeIds = assignEmployeeIds.split(',').map((s) => s.trim()).filter(Boolean);
    if (!managerId || employeeIds.length === 0) return alert('Manager and at least one employee ID are required');
    await api.put('/hr/feedback/manager-assignments', { managerEmployeeId: managerId, employeeIds });
    setShowAssign(false);
    setManagerId('');
    setAssignEmployeeIds('');
    alert('Manager assignment saved');
  };

  const columns: Column<Ticket>[] = [
    { key: 'ticket_number', header: 'Ticket #' },
    { key: 'employee_name', header: 'Employee', sortable: true },
    { key: 'subject', header: 'Subject' },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'created_at', header: 'Created', render: (r) => new Date(r.created_at).toLocaleDateString() },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <button className="text-blue-600 hover:underline text-sm" onClick={() => openDetail(r)}>
          Open
        </button>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Feedback Management</h1>
        <Button variant="secondary" onClick={() => setShowAssign(true)}>
          Feedback Manager Assignment
        </Button>
      </div>

      <Table columns={columns} rows={tickets} loading={loading} emptyMessage="No feedback tickets" />

      <Modal isOpen={!!detail} onClose={() => setDetail(null)} title={detail?.ticket_number}>
        {detail && (
          <div className="space-y-4">
            <div>
              <p className="font-semibold">{detail.subject}</p>
              <p className="text-sm text-gray-600">{detail.message}</p>
              <StatusBadge status={detail.status} />
            </div>
            <div className="max-h-56 overflow-y-auto space-y-2 border-t pt-3">
              {replies.map((r) => (
                <div key={r.id} className="text-sm bg-gray-50 rounded p-2">
                  <p className="text-gray-500 text-xs">
                    {new Date(r.created_at).toLocaleString()} {r.reply_status ? `— ${r.reply_status}` : ''}
                  </p>
                  <p>{r.reply_text}</p>
                </div>
              ))}
              {replies.length === 0 && <p className="text-sm text-gray-500">No replies yet</p>}
            </div>
            <textarea
              className="w-full border rounded-lg px-3 py-2"
              rows={3}
              placeholder="Reply..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
            />
            <div className="flex gap-2">
              <select
                className="border rounded-lg px-3 py-2 flex-1"
                value={replyStatus}
                onChange={(e) => setReplyStatus(e.target.value)}
              >
                <option value="Provide more data">Provide more data</option>
                <option value="Closed">Closed</option>
              </select>
              <Button onClick={sendReply}>Send</Button>
            </div>
            <a
              className="text-blue-600 text-sm hover:underline"
              href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/hr/feedback/${detail.id}/pdf`}
              target="_blank"
              rel="noreferrer"
            >
              Download conversation PDF
            </a>
          </div>
        )}
      </Modal>

      <Modal isOpen={showAssign} onClose={() => setShowAssign(false)} title="Feedback Manager Assignment">
        <div className="space-y-4">
          <LookupSelect
            label="Manager"
            value={managerId}
            onChange={(v) => setManagerId(v)}
            fetchOptions={fetchEmployeeOptions}
            placeholder="Search manager..."
            required
          />
          <div>
            <label className="text-sm font-medium text-gray-700">Employee IDs (comma-separated)</label>
            <textarea
              className="w-full border rounded-lg px-3 py-2 mt-1"
              rows={3}
              value={assignEmployeeIds}
              onChange={(e) => setAssignEmployeeIds(e.target.value)}
              placeholder="Paste employee UUIDs, comma-separated"
            />
          </div>
          <Button className="w-full" onClick={saveAssignment}>
            Save Assignment
          </Button>
        </div>
      </Modal>
    </div>
  );
}
